import React, { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSpinner, faChevronUp, faChevronDown, faFileExport, faFileCode, faFileCsv } from '@fortawesome/free-solid-svg-icons';
import './SurveyDetailsModal.css';

const SurveyDetailsModal = ({ isOpen, onClose, data, loading }) => {
    const [isUnselectedVisible, setIsUnselectedVisible] = useState(true);
    const [showExportMenu, setShowExportMenu] = useState(false);
    const exportContainerRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (exportContainerRef.current && 
                !exportContainerRef.current.contains(event.target)) {
                setShowExportMenu(false);
            }
        };

        if (showExportMenu) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showExportMenu]);

    if (!isOpen) return null;

    if (loading) {
        return (
            <div className="modal-overlay">
                <div className="survey-details-modal loading">
                    <FontAwesomeIcon icon={faSpinner} spin />
                    <p>Loading survey details...</p>
                </div>
            </div>
        );
    }

    const renderPriorityItems = (items = [], priority) => {
        if (!items.length) return null;
        
        return (
            <div className="priority-items">
                {items.map((item, index) => (
                    <div key={index} 
                        className="priority-item"
                        style={{ borderLeftColor: 
                            priority === 'high' ? '#d4380d' : 
                            priority === 'medium' ? '#faad14' : '#52c41a' 
                        }}>
                        <span className="item-number">{index + 1}.</span>
                        <span className="item-text">{item.SelectedEntries || 'No response'}</span>
                    </div>
                ))}
            </div>
        );
    };

    const renderBudgetPriorities = (priorities = []) => {
        if (!priorities.length) return <p>No budget priorities provided</p>;
        
        return priorities.map((priority, index) => (
            <p key={index}>
                <strong>{index + 1}st Priority:</strong> 
                <span>{priority || 'No response'}</span>
            </p>
        ));
    };

    const renderInnovationBarriers = (barriers = []) => {
        if (!barriers.length) return <p>No barriers provided</p>;
        
        const barrierTypes = ['Primary', 'Secondary', 'Tertiary'];
        return barriers.map((barrier, index) => (
            <p key={index}>
                <strong>{barrierTypes[index]} Barrier:</strong> 
                <span>{barrier || 'No response'}</span>
            </p>
        ));
    };

    const renderTargetMarkets = (markets = []) => {
        if (!markets.length) return <p>No target markets specified</p>;
        
        return (
            <ul>
                {markets.map((market, index) => (
                    <li key={index}>{market}</li>
                ))}
            </ul>
        );
    };

    const organizePriorityTopicsByDriver = (priorityTopics) => {
        const drivers = {
            "Business Operations": { high: [], medium: [], low: [], none: [] },
            "Market & Regulation": { high: [], medium: [], low: [], none: [] },
            "Product Maintenance": { high: [], medium: [], low: [], none: [] },
            "New Product": { high: [], medium: [], low: [], none: [] },
            "Emerging Technology": { high: [], medium: [], low: [], none: [] }
        };

        // Process each priority level
        ['high', 'medium', 'low'].forEach(priority => {
            if (priorityTopics[priority]) {
                priorityTopics[priority].forEach(item => {
                    if (drivers[item.DriverofInnovation]) {
                        drivers[item.DriverofInnovation][priority].push(item);
                    }
                });
            }
        });

        return drivers;
    };

    const renderPriorityTopics = (priorityTopics) => {
        const organizedTopics = organizePriorityTopicsByDriver(priorityTopics);

        return (
            <div className="priority-topics-section">
                <h2>Priority Topics by Driver</h2>
                {Object.entries(organizedTopics).map(([driver, priorities]) => (
                    <div key={driver} className="driver-section">
                        <h3>{driver}</h3>
                        <div className="priorities-container">
                            {['high', 'medium', 'low'].map(priority => (
                                <div key={priority} className="priority-column">
                                    <h4 className={`priority-header ${priority}`}>
                                        {priority.toUpperCase()} Priority
                                    </h4>
                                    <div className="priority-items">
                                        {priorities[priority].length > 0 ? (
                                            priorities[priority].map((item, index) => (
                                                <div 
                                                    key={index} 
                                                    className="priority-item"
                                                >
                                                    <span className="priority-number">{item.ProrityNumber && (item.ProrityNumber + ".")}</span>
                                                    <span className="topic-text">{item.SelectedEntries}</span>
                                                </div>
                                            ))
                                        ) : (
                                            <div className="no-items">No items</div>
                                        )}
                                    </div>
                                    
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    // Add this helper function
    const findMarketResponses = (priorityTopics) => {
        // Filter priorityTopics to find market-related responses
        const marketEntries = priorityTopics?.high?.filter(item => 
            item.DriverofInnovation === "Market & Regulation"
        ) || [];
        
        return {
            highest: marketEntries.find(item => item.ProrityNumber === 1)?.SelectedEntries || 'No response',
            second: marketEntries.find(item => item.ProrityNumber === 2)?.SelectedEntries || 'No response',
            third: marketEntries.find(item => item.ProrityNumber === 3)?.SelectedEntries || 'No response'
        };
    };

    // Update the Customer Markets section in the render
    const renderCustomerMarkets = () => {
        const markets = findMarketResponses(data.priorityTopics);
        
        return (
            <div className="content-section">
                <h2>Customer Markets</h2>
                <div className="info-grid">
                    <p>
                        <strong>Highest Revenue Market:</strong> 
                        <span>{markets.highest}</span>
                    </p>
                    <p>
                        <strong>Second Highest Revenue Market:</strong> 
                        <span>{markets.second}</span>
                    </p>
                    <p>
                        <strong>Third Highest Revenue Market:</strong> 
                        <span>{markets.third}</span>
                    </p>
                </div>
            </div>
        );
    };

    // Add these export functions
    const exportToJSON = () => {
        const jsonString = JSON.stringify(data, null, 2);
        const blob = new Blob([jsonString], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `survey-results-${data.RespondentID}.json`;
        link.click();
    };

    const exportToCSV = () => {
        // Convert the nested data to flat CSV format
        const csvRows = [
            // Headers
            ['Date', 'Respondent ID', 'Company Type', 'Priority Topics', 'Innovation Maturity', /* ... other headers */],
            // Data
            [
                new Date(data.Created).toLocaleDateString('en-IE'),
                data.RespondentID,
                data.CompanyType,
                // ... format other fields
            ]
        ];
        const csvString = csvRows.map(row => row.join(',')).join('\n');
        const blob = new Blob([csvString], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `survey-results-${data.RespondentID}.csv`;
        link.click();
    };

    const handlePrint = () => {
        window.print();
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="survey-details-modal" onClick={e => e.stopPropagation()}>
                <button className="close-button" onClick={onClose}>
                    <FontAwesomeIcon icon={faTimes} />
                </button>

                <div className="survey-results">
                    <div className="header-section">
                        <div className="header-main">
                            <h1>Innovation Survey Results</h1>
                            <div className="export-container" ref={exportContainerRef}>
                                <button 
                                    className="export-button"
                                    onClick={() => setShowExportMenu(!showExportMenu)}
                                >
                                    <FontAwesomeIcon icon={faFileExport} />
                                    Export
                                </button>
                                {showExportMenu && (
                                    <div className="export-menu">
                                        <button onClick={exportToJSON}>
                                            <FontAwesomeIcon icon={faFileCode} />
                                            Export as JSON
                                        </button>
                                        <button onClick={exportToCSV}>
                                            <FontAwesomeIcon icon={faFileCsv} />
                                            Export as CSV
                                        </button>
                                        {/* <button onClick={handlePrint}>
                                            <FontAwesomeIcon icon={faPrint} />
                                            Print
                                        </button> */}
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="header-details">
                            <p>Date: {new Date(data.Created || data.createdDateTime).toLocaleDateString('en-IE', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric'
                            })}</p>
                            <p className="respondent-id">ID: {data.RespondentID}</p>
                        </div>
                    </div>

                    {/* Company Information Section - no number */}
                    <div className="survey-section">
                        <h3>Company Information</h3>
                        <div className="content-box">
                            <div className="info-row">
                                <span className="info-label">Company Type:</span>
                                <span className="info-value">{data.CompanyType || 'No response'}</span>
                            </div>
                            <div className="info-row">
                                <span className="info-label">Company Structure:</span>
                                <span className="info-value">{data.CompanyStructure || 'No response'}</span>
                            </div>
                            <div className="info-row">
                                <span className="info-label">Number of FTEs in Ireland:</span>
                                <span className="info-value">{data.NumberofFTEsinIreland || 'No response'}</span>
                            </div>
                        </div>
                    </div>

                    {/* Add this new section right after the Company Information section and before Question 1 */}
                    <div className="survey-section priority-topics">
                        <h3>Priority Topics</h3>
                        <div className="content-box">
                            <div className="pillars-container">
                                {/* Business Operations Pillar */}
                                <div className="pillar">
                                    <div className="pillar-header business-ops">Business Operations</div>
                                    <div className="priority-levels">
                                        <div className="priority high">
                                            <h4>HIGH</h4>
                 
                                            {data.priorityTopics?.high
                                                ?.filter(item => item.DriverofInnovation === "Business Operations")
                                                .sort((a, b) => (a.ProrityNumber || 0) - (b.ProrityNumber || 0))
                                                .map((item, index) => {
                                    
                                                    return (
                                                        <div key={index} className="priority-item">
                                                            {item.ProrityNumber && Number(item.ProrityNumber) <= 3 ? (
                                                                <span className="priority-number">{item.ProrityNumber}.</span>
                                                            ) : null}
                                                            <span className="topic-text">{item.SelectedEntries}</span>
                                                        </div>
                                                    );
                                                })}
                                        </div>
                                        <div className="priority medium">
                                            <h4>MEDIUM</h4>
                                            {data.priorityTopics?.medium
                                                ?.filter(item => item.DriverofInnovation === "Business Operations")
                                                .sort((a, b) => a.ProrityNumber - b.ProrityNumber)
                                                .map((item, index) => (
                                                    <div key={index} className="priority-item">
                                                        <span className="topic-text">{item.SelectedEntries}</span>
                                                    </div>
                                                ))}
                                        </div>
                                        <div className="priority low">
                                            <h4>LOW</h4>
                                            {data.priorityTopics?.low
                                                ?.filter(item => item.DriverofInnovation === "Business Operations")
                                                .sort((a, b) => a.ProrityNumber - b.ProrityNumber)
                                                .map((item, index) => (
                                                    <div key={index} className="priority-item">
                                                        
                                                        <span className="topic-text">{item.SelectedEntries}</span>
                                                    </div>
                                                ))}
                                        </div>
                                        
                                    </div>
                                </div>

                                {/* Market & Regulation Pillar */}
                                <div className="pillar">
                                    <div className="pillar-header market-reg">Market & Regulation</div>
                                    <div className="priority-levels">
                                        <div className="priority high">
                                            <h4>HIGH</h4>
                                            {data.priorityTopics?.high
                                                ?.filter(item => item.DriverofInnovation === "Market & Regulation")
                                                .sort((a, b) => a.ProrityNumber - b.ProrityNumber)
                                                .map((item, index) => {
                                                    return (
                                                        <div key={index} className="priority-item">
                                                            {item.ProrityNumber && Number(item.ProrityNumber) <= 3 ? (
                                                                <span className="priority-number">{item.ProrityNumber}.</span>
                                                            ) : null}
                                                            <span className="topic-text">{item.SelectedEntries}</span>
                                                        </div>
                                                    );
                                                })}
                                        </div>
                                        <div className="priority medium">
                                            <h4>MEDIUM</h4>
                                            {data.priorityTopics?.medium
                                                ?.filter(item => item.DriverofInnovation === "Market & Regulation")
                                                .sort((a, b) => a.ProrityNumber - b.ProrityNumber)
                                                .map((item, index) => (
                                                    <div key={index} className="priority-item">
                                                      
                                                        <span className="topic-text">{item.SelectedEntries}</span>
                                                    </div>
                                                ))}
                                        </div>
                                        <div className="priority low">
                                            <h4>LOW</h4>
                                            {data.priorityTopics?.low
                                                ?.filter(item => item.DriverofInnovation === "Market & Regulation")
                                                .sort((a, b) => a.ProrityNumber - b.ProrityNumber)
                                                .map((item, index) => (
                                                    <div key={index} className="priority-item">
                                                        <span className="topic-text">{item.SelectedEntries}</span>
                                                    </div>
                                                ))}
                                        </div>
                                      
                                    </div>
                                </div>

                                {/* Product/Service Maintenance Pillar */}
                                <div className="pillar">
                                    <div className="pillar-header product-maint">Product/Service Maintenance</div>
                                    <div className="priority-levels">
                                        <div className="priority high">
                                            <h4>HIGH</h4>
                                            {data.priorityTopics?.high
                                                ?.filter(item => item.DriverofInnovation === "Product Maintenance")
                                                .sort((a, b) => a.ProrityNumber - b.ProrityNumber)
                                                .map((item, index) => (
                                                    <div key={index} className="priority-item">
                                                       <span className="priority-number">{item.ProrityNumber}</span>
                                                        <span className="topic-text">{item.SelectedEntries}</span>
                                                    </div>
                                                ))}
                                        </div>
                                        <div className="priority medium">
                                            <h4>MEDIUM</h4>
                                            {data.priorityTopics?.medium
                                                ?.filter(item => item.DriverofInnovation === "Product Maintenance")
                                                .sort((a, b) => a.ProrityNumber - b.ProrityNumber)
                                                .map((item, index) => (
                                                    <div key={index} className="priority-item">
                                                        <span className="topic-text">{item.SelectedEntries}</span>
                                                    </div>
                                                ))}
                                        </div>
                                        <div className="priority low">
                                            <h4>LOW</h4>
                                            {data.priorityTopics?.low
                                                ?.filter(item => item.DriverofInnovation === "Product Maintenance")
                                                .sort((a, b) => a.ProrityNumber - b.ProrityNumber)
                                                .map((item, index) => (
                                                    <div key={index} className="priority-item">
                                                        <span className="topic-text">{item.SelectedEntries}</span>
                                                    </div>
                                                ))}
                                        </div>
                                        
                                    </div>
                                </div>

                                {/* New Product/Service Dev Pillar */}
                                <div className="pillar">
                                    <div className="pillar-header new-product">New Product/Service Dev</div>
                                    <div className="priority-levels">
                                        <div className="priority high">
                                            <h4>HIGH</h4>
                                            {data.priorityTopics?.high
                                                ?.filter(item => item.DriverofInnovation === "New Product")
                                                .sort((a, b) => a.ProrityNumber - b.ProrityNumber)
                                                .map((item, index) => (
                                                    <div key={index} className="priority-item">
                                                        {item.ProrityNumber && item.ProrityNumber <= 3 && (
                                                            <span className="priority-number">{item.ProrityNumber}.</span>
                                                        )}
                                                        <span className="topic-text">{item.SelectedEntries}</span>
                                                    </div>
                                                ))}
                                        </div>
                                        <div className="priority medium">
                                            <h4>MEDIUM</h4>
                                            {data.priorityTopics?.medium
                                                ?.filter(item => item.DriverofInnovation === "New Product")
                                                .sort((a, b) => a.ProrityNumber - b.ProrityNumber)
                                                .map((item, index) => (
                                                    <div key={index} className="priority-item">
                                                        <span className="topic-text">{item.SelectedEntries}</span>
                                                    </div>
                                                ))}
                                        </div>
                                        <div className="priority low">
                                            <h4>LOW</h4>
                                            {data.priorityTopics?.low
                                                ?.filter(item => item.DriverofInnovation === "New Product")
                                                .sort((a, b) => a.ProrityNumber - b.ProrityNumber)
                                                .map((item, index) => (
                                                    <div key={index} className="priority-item">
                                                        <span className="topic-text">{item.SelectedEntries}</span>
                                                    </div>
                                                ))}
                                        </div>
                                       
                                    </div>
                                </div>

                                {/* Emerging/Disruptive Pillar */}
                                <div className="pillar">
                                    <div className="pillar-header emerging">Emerging/Disruptive</div>
                                    <div className="priority-levels">
                                        <div className="priority high">
                                            <h4>HIGH</h4>
                                            {data.priorityTopics?.high
                                                ?.filter(item => item.DriverofInnovation === "Emerging Technology")
                                                .sort((a, b) => a.ProrityNumber - b.ProrityNumber)
                                                .map((item, index) => (
                                                    <div key={index} className="priority-item">
                                                        {item.ProrityNumber && item.ProrityNumber <= 3 && (
                                                            <span className="priority-number">{item.ProrityNumber}.</span>
                                                        )}
                                                        <span className="topic-text">{item.SelectedEntries}</span>
                                                    </div>
                                                ))}
                                        </div>
                                        <div className="priority medium">
                                            <h4>MEDIUM</h4>
                                            {data.priorityTopics?.medium
                                                ?.filter(item => item.DriverofInnovation === "Emerging Technology")
                                                .sort((a, b) => a.ProrityNumber - b.ProrityNumber)
                                                .map((item, index) => (
                                                    <div key={index} className="priority-item">
                                                        <span className="topic-text">{item.SelectedEntries}</span>
                                                    </div>
                                                ))}
                                        </div>
                                        <div className="priority low">
                                            <h4>LOW</h4>
                                            {data.priorityTopics?.low
                                                ?.filter(item => item.DriverofInnovation === "Emerging Technology")
                                                .sort((a, b) => a.ProrityNumber - b.ProrityNumber)
                                                .map((item, index) => (
                                                    <div key={index} className="priority-item">
                                                        
                                                        <span className="topic-text">{item.SelectedEntries}</span>
                                                    </div>
                                                ))}
                                        </div>
                                        
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* New section for unselected topics */}
                    <div className="survey-section">
                        <div className="unselected-topics">
                            <div className="unselected-header" onClick={() => setIsUnselectedVisible(!isUnselectedVisible)}>
                                <h3>Unselected Topics</h3>
                                <button className={`collapse-button ${isUnselectedVisible ? 'open' : ''}`}>
                                    <FontAwesomeIcon icon={isUnselectedVisible ? faChevronUp : faChevronDown} />
                                </button>
                            </div>
                            
                            <div className={`pillars-container ${isUnselectedVisible ? 'visible' : ''}`}>
                                {/* Business Operations */}
                                <div className="pillar">
                                    <div className="pillar-header business-ops">Business Operations</div>
                                    <div className="priority-levels">
                                        <div className="priority none">
                                            {data.priorityTopics?.none
                                                ?.filter(item => item.DriverofInnovation === "Business Operations")
                                                .map((item, index) => (
                                                    <div key={index} className="priority-item">
                                                        <span className="topic-text">{item.SelectedEntries}</span>
                                                    </div>
                                                ))}
                                        </div>
                                    </div>
                                </div>

                                {/* Market & Regulation */}
                                <div className="pillar">
                                    <div className="pillar-header market-reg">Market & Regulation</div>
                                    <div className="priority-levels">
                                        <div className="priority none">
                                            {data.priorityTopics?.none
                                                ?.filter(item => item.DriverofInnovation === "Market & Regulation")
                                                .map((item, index) => (
                                                    <div key={index} className="priority-item">
                                                        <span className="topic-text">{item.SelectedEntries}</span>
                                                    </div>
                                                ))}
                                        </div>
                                    </div>
                                </div>

                                {/* Product/Service Maintenance */}
                                <div className="pillar">
                                    <div className="pillar-header product-maint">Product/Service Maintenance</div>
                                    <div className="priority-levels">
                                        <div className="priority none">
                                            {data.priorityTopics?.none
                                                ?.filter(item => item.DriverofInnovation === "Product Maintenance")
                                                .map((item, index) => (
                                                    <div key={index} className="priority-item">
                                                        <span className="topic-text">{item.SelectedEntries}</span>
                                                    </div>
                                                ))}
                                        </div>
                                    </div>
                                </div>

                                {/* New Product/Service Dev */}
                                <div className="pillar">
                                    <div className="pillar-header new-product">New Product/Service Dev</div>
                                    <div className="priority-levels">
                                        <div className="priority none">
                                            {data.priorityTopics?.none
                                                ?.filter(item => item.DriverofInnovation === "New Product")
                                                .map((item, index) => (
                                                    <div key={index} className="priority-item">
                                                        <span className="topic-text">{item.SelectedEntries}</span>
                                                    </div>
                                                ))}
                                        </div>
                                    </div>
                                </div>

                                {/* Emerging/Disruptive */}
                                <div className="pillar">
                                    <div className="pillar-header emerging">Emerging/Disruptive</div>
                                    <div className="priority-levels">
                                        <div className="priority none">
                                            {data.priorityTopics?.none
                                                ?.filter(item => item.DriverofInnovation === "Emerging Technology")
                                                .map((item, index) => (
                                                    <div key={index} className="priority-item">
                                                        <span className="topic-text">{item.SelectedEntries}</span>
                                                    </div>
                                                ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Innovation Collaboration Section - now 1. */}
                    <div className="survey-section">
                        <h3>1. Innovation Collaboration</h3>
                        <div className="content-box">
                            <h4>{data.collaborationQuestion}</h4>
                            {data.collaborationTypes && data.collaborationTypes.length > 0 ? (
                                <ul className="collaboration-list">
                                    {data.collaborationTypes.map((type, index) => (
                                        <li key={index}>{type}</li>
                                    ))}
                                </ul>
                            ) : (
                                <p>No collaboration types selected</p>
                            )}
                        </div>
                    </div>

                    {/* Best Outcomes Section - now 2. */}
                    <div className="survey-section">
                        <h3>2. Best Outcomes from Collaborations</h3>
                        <div className="content-box">
                            <h4>{data.bestOutcomesQuestion}</h4>
                            {data.bestOutcomes && data.bestOutcomes.length > 0 ? (
                                <div className="outcomes-list">
                                    {data.bestOutcomes.map((outcome, index) => (
                                        <div key={index} className="outcome-item">
                                            <span className="outcome-label">
                                                {index === 0 ? 'Best Outcome (1st):' : 'Good Outcome (2nd):'}
                                            </span>
                                            <span className="outcome-value">{outcome}</span>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p>No outcomes specified</p>
                            )}
                        </div>
                    </div>

                    {/* Innovation Barriers Section - now 3. */}
                    <div className="survey-section">
                        <h3>3. Innovation Barriers</h3>
                        <div className="content-box">
                            <h4>{data.barriersQuestion}</h4>
                            {data.barriers && data.barriers.length > 0 ? (
                                <div className="barriers-list">
                                    {data.barriers.map((barrier, index) => (
                                        <div key={index} className="barrier-item">
                                            <span className={`dot ${
                                                index === 0 ? 'red' : 
                                                index === 1 ? 'yellow' : 
                                                'green'
                                            }`}></span>
                                            <span className="barrier-label">
                                                {index === 0 ? 'Biggest:' :
                                                 index === 1 ? 'Second:' :
                                                 'Third:'}
                                            </span>
                                            <span className="barrier-value">{barrier}</span>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p>No barriers specified</p>
                            )}
                        </div>
                    </div>

                    {/* Innovation Maturity Section - now 4. */}
                    <div className="survey-section">
                        <h3>4. Innovation Maturity</h3>
                        <div className="content-box">
                            <h4>What is the current state of innovation maturity within your firm?</h4>
                            <div className="maturity-response">
                                {data.InnovationMaturity || 'No response provided'}
                            </div>
                        </div>
                    </div>

                    {/* Drivers of Innovation Section - now 5. */}
                    <div className="survey-section">
                        <h3>5. Drivers of Innovation</h3>
                        <div className="content-box">
                            <h4>{data.driversQuestion}</h4>
                            <div className="drivers-list">
                                {data.drivers && data.drivers.length > 0 ? (
                                    data.drivers.map((driver, index) => (
                                        <div key={index} className="driver-item">
                                            <span className="rank">{driver.rank}</span>
                                            <span className="driver-name">{driver.name}</span>
                                        </div>
                                    ))
                                ) : (
                                    <p>No drivers specified</p>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Innovation Vision Section - now 6. */}
                    <div className="survey-section">
                        <h3>6. Innovation Vision</h3>
                        <div className="content-box">
                            <h4>Does your firm have a clearly articulated vision statement for innovation?</h4>
                            <div className="vision-response">
                                {data.InnovationVision ? 
                                    data.InnovationVision.charAt(0).toUpperCase() + data.InnovationVision.slice(1) 
                                    : 'No response provided'}
                            </div>
                        </div>
                    </div>

                    {/* Market Position Section */}
                    <div className="survey-section">
                        <h3>7. Market Position</h3>
                        <div className="content-box">
                            <h4>{data.marketPositionQuestion}</h4>
                            <div className="position-response">
                                {data.marketPosition}
                            </div>
                        </div>
                    </div>

                    {/* Success Measures Section */}
                    <div className="survey-section">
                        <h3>8. Success Measures</h3>
                        <div className="content-box">
                            <h4>{data.successMeasuresQuestion}</h4>
                            <div className="success-measures-list">
                                {data.successMeasures && data.successMeasures.length > 0 ? (
                                    data.successMeasures.map((measure, index) => (
                                        <div key={index} className="measure-item">
                                            <span className="rank">{index + 1}</span>
                                            <span className="measure-name">{measure}</span>
                                        </div>
                                    ))
                                ) : (
                                    <p>No measures specified</p>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Product Strategy Section */}
                    <div className="survey-section">
                        <h3>9. Product Strategy</h3>
                        <div className="content-box">
                            <h4>{data.productStrategyQuestion}</h4>
                            <div className="strategy-categories">
                                {data.productStrategy?.categories.map((category, index) => (
                                    <div key={index} className="strategy-item">
                                        <div className="strategy-header">
                                            <span className="strategy-name">{category.name}</span>
                                            <span className="strategy-percentage">{category.percentage}%</span>
                                        </div>
                                        <p className="strategy-description">{category.description}</p>
                                        <div className="percentage-bar">
                                            <div 
                                                className="percentage-fill" 
                                                style={{ width: `${category.percentage}%` }}
                                            ></div>
                                        </div>
                                    </div>
                                ))}
                                <div className="strategy-total">
                                    Total: {data.productStrategy?.total}%
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Top Revenue Markets Section */}
                    <div className="survey-section">
                        <h3>10. Top Revenue Markets</h3>
                        <div className="content-box">
                            {data.topMarkets && data.topMarkets.length > 0 ? (
                                <div className="market-list">
                                    {data.topMarkets.map((item, index) => (
                                        <div key={index} className="market-item">
                                            <span className={`rank-dot rank-${index + 1}`}></span>
                                            <span className="market-rank">
                                                {index === 0 ? "Highest" : index === 1 ? "Second" : "Third"}:
                                            </span>
                                            <span className="market-name">{item.market}</span>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p>No market data available</p>
                            )}
                        </div>
                    </div>

                    {/* Future Market Investment Section */}
                    <div className="survey-section">
                        <h3>11. Future Market Investment</h3>
                        <div className="content-box">
                            {data.futureMarkets && data.futureMarkets.length > 0 ? (
                                <div className="future-markets">
                                    <p className="future-markets-intro">Planning to enter or increase investment in:</p>
                                    <div className="markets-grid">
                                        {data.futureMarkets.map((market, index) => (
                                            <div key={index} className="market-chip">
                                                <span className="market-icon">🎯</span>
                                                {market}
                                            </div>
                                        ))}
                                    </div>
                                    
                                    {/* Add this new section for Asian Countries */}
                                    {data.asianCountries && (
                                        <div className="asian-countries-section">
                                            <h4>Asian Countries of Interest:</h4>
                                            <div className="asian-countries-grid">
                                                {data.asianCountries.first && (
                                                    <div className="country-chip">
                                                        <span className="country-icon">🌏</span>
                                                        {data.asianCountries.first}
                                                    </div>
                                                )}
                                                {data.asianCountries.second && (
                                                    <div className="country-chip">
                                                        <span className="country-icon">🌏</span>
                                                        {data.asianCountries.second}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <p>No future market plans specified</p>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SurveyDetailsModal; 